import { supabase } from '@/integrations/supabase/client';
import { extractDocumentText } from './pdf-extract';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface KBFolder {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  parent_folder_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface KBDocument {
  id: string;
  company_id: string;
  folder_id: string | null;
  name: string;
  description: string | null;
  file_path: string | null;
  file_type: string | null;
  file_size: number | null;
  jurisdiction: string | null;
  regulation_type: string | null;
  tags: string[] | null;
  extracted_text: string | null;
  status: 'uploading' | 'processing' | 'ready' | 'failed';
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UploadDocumentOptions {
  companyId: string;
  uploadedBy: string;
  folderId?: string | null;
  description?: string;
  jurisdiction?: string;
  regulationType?: string;
  tags?: string[];
}

// Default folders seeded for new companies
export const DEFAULT_FOLDERS = [
  { name: 'Data Privacy & GDPR', description: 'Data protection regulations, GDPR, CCPA and related compliance' },
  { name: 'HR & Employment Law', description: 'Employment regulations, workplace policies and labour law' },
  { name: 'Financial Compliance', description: 'Financial regulations, reporting requirements and audit standards' },
  { name: 'Health & Safety', description: 'Occupational health, safety regulations and environmental standards' },
  { name: 'Industry-Specific Regulations', description: 'Regulations specific to your industry or sector' },
];

// ─── Folders ─────────────────────────────────────────────────────────────────

export async function getFolders(companyId: string): Promise<KBFolder[]> {
  const { data, error } = await supabase
    .from('knowledge_base_folders')
    .select('*')
    .eq('company_id', companyId)
    .order('name', { ascending: true });

  if (error) throw error;
  return (data ?? []) as KBFolder[];
}

export async function createFolder(
  companyId: string,
  name: string,
  createdBy: string,
  parentFolderId?: string | null,
  description?: string
): Promise<KBFolder> {
  const { data, error } = await supabase
    .from('knowledge_base_folders')
    .insert({
      company_id: companyId,
      name: name.trim(),
      description: description ?? null,
      parent_folder_id: parentFolderId ?? null,
      created_by: createdBy,
    })
    .select()
    .single();

  if (error) throw error;
  return data as KBFolder;
}

export async function deleteFolder(folderId: string): Promise<void> {
  const { error } = await supabase
    .from('knowledge_base_folders')
    .delete()
    .eq('id', folderId);

  if (error) throw error;
}

export async function seedDefaultFolders(companyId: string, createdBy: string): Promise<void> {
  const existing = await getFolders(companyId);
  if (existing.length > 0) return; // Already seeded

  for (const folder of DEFAULT_FOLDERS) {
    await createFolder(companyId, folder.name, createdBy, null, folder.description);
  }
}

// ─── Documents ───────────────────────────────────────────────────────────────

export async function getDocuments(
  companyId: string,
  folderId?: string | null
): Promise<KBDocument[]> {
  let query = supabase
    .from('knowledge_base_documents')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (folderId === null) {
    // Unfiled documents
    query = query.is('folder_id', null);
  } else if (folderId) {
    query = query.eq('folder_id', folderId);
  }
  // If folderId is undefined, return all documents

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as KBDocument[];
}

export async function uploadDocument(
  file: File,
  options: UploadDocumentOptions
): Promise<KBDocument> {
  const { companyId, uploadedBy, folderId, description, jurisdiction, regulationType, tags } = options;

  // 1. Extract text from the file
  const extractedText = await extractDocumentText(file);

  // 2. Build storage path
  const ext = file.name.split('.').pop() ?? 'pdf';
  const uniqueName = `${crypto.randomUUID()}.${ext}`;
  const storagePath = `${companyId}/${uniqueName}`;

  // 3. Upload file to Supabase Storage
  const { error: storageError } = await supabase.storage
    .from('knowledge-base-documents')
    .upload(storagePath, file, {
      contentType: file.type,
      upsert: false,
    });

  if (storageError) throw storageError;

  // 4. Insert document record
  const { data: doc, error: insertError } = await supabase
    .from('knowledge_base_documents')
    .insert({
      company_id: companyId,
      folder_id: folderId ?? null,
      name: file.name,
      description: description ?? null,
      file_path: storagePath,
      file_type: file.type,
      file_size: file.size,
      jurisdiction: jurisdiction ?? null,
      regulation_type: regulationType ?? null,
      tags: tags ?? null,
      extracted_text: extractedText || null,
      status: extractedText ? 'processing' : 'failed',
      uploaded_by: uploadedBy,
    })
    .select()
    .single();

  if (insertError) throw insertError;

  // 5. Trigger edge function to generate embeddings (fire-and-forget)
  if (extractedText) {
    const apiKey = localStorage.getItem('openai_api_key');
    supabase.functions
      .invoke('generate-knowledge-embeddings', {
        body: { document_id: doc.id, api_key: apiKey },
      })
      .catch(err => console.error('[KB] Failed to invoke embedding function:', err));
  }

  return doc as KBDocument;
}

export async function deleteDocument(document: KBDocument): Promise<void> {
  // Delete from storage
  if (document.file_path) {
    await supabase.storage
      .from('knowledge-base-documents')
      .remove([document.file_path]);
  }

  // Delete DB record (cascades to embeddings)
  const { error } = await supabase
    .from('knowledge_base_documents')
    .delete()
    .eq('id', document.id);

  if (error) throw error;
}

export async function getDocumentDownloadUrl(filePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('knowledge-base-documents')
    .createSignedUrl(filePath, 60 * 60); // 1-hour expiry

  if (error) throw error;
  return data.signedUrl;
}

// ─── Company knowledge profile (wizard data) ─────────────────────────────────

export async function getCompanyKnowledgeData(companyId: string): Promise<Record<string, string> | null> {
  const { data, error } = await supabase
    .from('companies')
    .select('knowledge_data')
    .eq('id', companyId)
    .single();

  if (error) throw error;
  return (data?.knowledge_data as Record<string, string>) ?? null;
}

export async function saveCompanyKnowledgeData(
  companyId: string,
  answers: Record<string, string>
): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .update({ knowledge_data: answers })
    .eq('id', companyId);

  if (error) throw error;
  // Clear legacy localStorage copy
  localStorage.removeItem('company_knowledge');
}
